import React, { useState, useEffect } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { BarChart3, MessageSquare, Users, Briefcase } from "lucide-react";
import { getDashboardDetails, npsHandle } from "../../redux/action";
import QuickActions from "../../component/Dashboard/QuickActions";
import Footer from "../../component/Layout/Footer";
import ExpiryPopUp from "../ExpiryPopUp";
import GradientAreaChart from "../../component/Dashboard/GradientAreaChart";
import StatsCardComponent from "./StatsCard";
import PositionStatusComponent from "./PositionStatus";
import CommunicationChannelsComponent from "./CommunicationChannels";
import InterviewAnalytics from "./InterviewAnalytics";
import MonthlyTrends from "./MonthlyTrends";
import WeeklyActivity from "./WeeklyActivity";
import AverageInterviewDuration from "./AverageInterviewDuration";
import { SurveyProvider } from "../context/SurveyContext";
import { SurveyModal } from "../../component/Survey";
import L2ConversationofCandidates from "./L2ConversationofCandidates";

const DashboardRevamped = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expirePopupVisibility, setExpirePopupVisibility] = useState(false);
  const dashboardData = useSelector((state) => state.Dashboard.lists);
  const productData = useSelector((state) => state?.Profile?.productData);
  const productId =
    productData?.find(
      (p) =>
        (p?.status === "Subscribed" || p?.status === "Expired") &&
        p?.subdomain === "InCScreen",
    )?._id ?? null;

  useEffect(() => {
    dispatch(getDashboardDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(npsHandle({ action: "LOGIN", productId }));
  }, [dispatch, productId]);

  useEffect(() => {
    if (
      dashboardData?.validTill &&
      moment().isAfter(moment(dashboardData.validTill).endOf("day"))
    ) {
      setExpirePopupVisibility(true);
    }
  }, [dashboardData?.validTill]);

  // Sample data for the dashboard
  const interviewStats = [
    {
      label: "Cancelled",
      value: dashboardData?.interview?.canceled || 0,
      color: "#EF4444",
    },
    {
      label: "Completed",
      value: dashboardData?.interview?.completed || 0,
      color: "#187708",
    },
    {
      label: "Disqualified",
      value: dashboardData?.interview?.disqualified || 0,
      color: "#D3040A",
    },
    {
      label: "Follow-up",
      value: dashboardData?.interview?.followUp || 0,
      color: "#D68000",
    },
    {
      label: "In Progress",
      value: dashboardData?.interview?.inProgress || 0,
      color: "#006B6F",
    },
    {
      label: "No Response",
      value: dashboardData?.interview?.noResponse || 0,
      color: "#626262",
    },
    {
      label: "Pending",
      value: dashboardData?.interview?.pending || 0,
      color: "#9b9b9b",
    },
    {
      label: "Resume Rejected",
      value: dashboardData?.interview?.resumeRejected || 0,
      color: "#D3040A",
    },
    {
      label: "Qualified",
      value: dashboardData?.interview?.qualified || 0,
      color: "#187708",
    },
    {
      label: "Scheduled",
      value: dashboardData?.interview?.scheduled || 0,
      color: "#133F7D",
    },
  ];

  const quickActionsData = [
    {
      type: "create",
      label: "Create Position",
      icon: <Briefcase className="w-5 h-5" />,
      onClick: () => navigate("/create-position"),
    },
    {
      type: "setup",
      label: "Set Up Interview",
      icon: <MessageSquare className="w-5 h-5" />,
      onClick: () => navigate("/create-questions"),
    },
    {
      type: "upload",
      label: "Add Candidates",
      icon: <Users className="w-5 h-5" />,
      onClick: () => navigate("/create-candidate"),
    },
  ];

  const weeklyActivityData = dashboardData?.weeklyActivityData || [];

  const recentActivities = dashboardData?.recentActivities || [];

  return (
    <SurveyProvider autoOpenAfterMs={30000} autoOpenFlag={true}>
      <SurveyModal />
      <div className="cpp-wrapper d-flex flex-column h-100">
        <div className="f-fh f-sem-bol">Dashboard</div>
        <div className="f-14 f-reg mb-2 list-subtitle">
          Welcome back! Here's your recruitment overview
        </div>
        <div className="dashboard-container">
          <div className="container-fluid">
            {/* Stats Grid */}
            <StatsCardComponent />
            {/* Charts Row */}

            <div className="main-all-chart-container">
              <PositionStatusComponent dashboardData={dashboardData} />
              <CommunicationChannelsComponent dashboardData={dashboardData} />
              <MonthlyTrends dashboardData={dashboardData} />
              <AverageInterviewDuration dashboardData={dashboardData} />
              <InterviewAnalytics dashboardData={dashboardData} />
              <L2ConversationofCandidates dashboardData={dashboardData} />
            </div>

            {weeklyActivityData?.length > 0 && (
              <WeeklyActivity
                weeklyActivityData={weeklyActivityData}
                recentActivities={recentActivities}
              />
            )}
            {/* Performance and Actions Row */}
            <div className="row g-4">
              <div className="col-12 col-xl-12">
                <QuickActions
                  actions={quickActionsData}
                  title="Quick Actions"
                  subtitle="Common tasks and shortcuts"
                />
              </div>
            </div>
          </div>
        </div>
        <Footer />
        {expirePopupVisibility && (
          <ExpiryPopUp
            expirePopupVisibility={expirePopupVisibility}
            setExpirePopupVisibility={setExpirePopupVisibility}
            buttonName="Okay"
          />
        )}
      </div>
    </SurveyProvider>
  );
};

export default DashboardRevamped;
